import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { asapScheduler, Observable, scheduled } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { Profile } from '../classes/profile';
import { ProfileService } from '../services/profile.service';
import { LoggerService } from '../services/logger.service';

@Injectable({
  providedIn: 'root'
})
export class RoleGuard implements CanActivate {
  constructor(private profileSvc: ProfileService, private router: Router, private logger: LoggerService) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    this.logger.debug(`[AuthFlow][RoleGuard] evaluating route: ${state?.url || 'unknown'}`, 'auth');
    return this.profileSvc.getProfileObs().pipe(
      switchMap((user: Profile) =>
        user
          ? scheduled([ user ], asapScheduler)
          : this.profileSvc.postProfile().pipe(
              tap(() => this.logger.debug('[AuthFlow][RoleGuard] no cached profile; fetched profile from API', 'auth')),
              map((response) => response.body as Profile)
            )
      ),
      map((user: Profile) => {
        const rolesAllowed = route.data ? route.data.rolesAllowed : undefined;
        this.logger.debug(
          `[AuthFlow][RoleGuard] rolesAllowed=${JSON.stringify(rolesAllowed || [])} userRoles=${JSON.stringify(user?.userRoles || [])}`,
          'auth'
        );
        if (rolesAllowed && rolesAllowed.length) {
          return user.userRoles && user.userRoles.some((userRole: string) => rolesAllowed.includes(userRole));
        }
        return true;
      }),
      tap((isAuthorized: boolean) => {
        this.logger.debug(`[AuthFlow][RoleGuard] authorization result=${isAuthorized} for route=${state?.url || 'unknown'}`, 'auth');
        if (!isAuthorized) {
          this.logger.warn('[AuthFlow][RoleGuard] navigating to /unauthorized');
          this.router.navigate([ '/unauthorized' ]);
        }
      })
    );
  }
}
